//ExportMap.ts =================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as GetSearchResults from "../Search/GetSearchResults";
import * as MainMap from "../MainMap";
import * as Settings from "../Settings";
import * as SnackBar from "../Util/SnackBar";
import * as Spinner from "../Util/Spinner";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as Util_MUI from "../Util/Util_MUI";
import * as WidgetHeading from "../Widgets/WidgetHeading";

import { Button, Paper, TextField, Typography } from "@mui/material";

import React from "react";

import * as geoprocessor from "@arcgis/core/rest/geoprocessor";
import JobInfo from "@arcgis/core/rest/support/JobInfo";
import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";

const toolName: string = "Export Map";
let exportFormatValue: string = "Shapefile";

const exportFormatLabels: string[] = [
   "AutoCAD R2010 (.dwg)", "AutoCAD R2013 (.dwg)", "AutoCAD R2018 (.dwg)",
   "AutoCAD R2010 (.dxf)", "AutoCAD R2013 (.dxf)", "AutoCAD R2018 (.dxf)",
   "ESRI File Geodatabase (.gdb)", "ESRI Shapefile (.shp)",
   "Microstation (.dgn)"];
//"Keyhole Markup (.kmz)", "Microstation (.dgn)"];

const exportFormatValues: string[] = [
   "DWG_R2010", "DWG_R2013", "DWG_R2018",
   "DXF_R2010", "DXF_R2013", "DXF_R2018",
   "File_Geodatabase", "Shapefile",
   "DGN_V8"];
//"KML", "DGN_V8"];

//Input arguments.
interface iProps {
}

//Render function.
export function Render(props: iProps) {
   Util.logMsg("Top of ExportMap.Render");

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
      Util.logMsg("ExportMap mounted");
   }, []);

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Export format changed.
   function formatChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      exportFormatValue = event.target.value;
      refresh();
   }

   //Get exported data.
   function downloadFile() {

      //Project to local projection if needed.
      let mbr = MainMap.activeView.extent;

      if (mbr.spatialReference.wkid !== Settings.wkID) {
         const outSpatialReference = new SpatialReference({ wkid: Settings.wkID });
         mbr = projection.project(mbr, outSpatialReference) as __esri.Extent;
      }

      mbr.xmin = Util.round(mbr.xmin);
      mbr.ymin = Util.round(mbr.ymin);
      mbr.xmax = Util.round(mbr.xmax);
      mbr.ymax = Util.round(mbr.ymax);

      //Get list of visible layers.
      const visLays: GetSearchResults.iSelectableLayers[] = Util_App.getVisibleSelectOnMapLayerNames("All");
      if (visLays.length === 0) {
         SnackBar.show("No visible map layers at current map scale", 2);
         return;
      }

      const layerNames: string[] = [];
      for (const visLay of visLays) {
         layerNames.Add(visLay.name);
      }
      const layerNameList: string = layerNames.join(",");

      //Set max number of records to download.
      let maxRec: number = Settings.maxExportPublic;
      if (Settings.security.accessLevel >= Settings.security.accessLevel_exportMapLayers) {
         maxRec = Settings.maxExportSecured;
      }

      //Parameters below are case sensitive and must match the REST endpoint geoprocessing service.
      const params = {
         layerNames: layerNameList,
         formatType: exportFormatValue,
         clip: true,
         clipExtent: mbr.xmin + "," + mbr.ymin + "," + mbr.xmax + "," + mbr.ymax,
         maxRec: maxRec.toString(),
      };

      //Show spinner.
      if (Spinner.busy) {
         return; //Quit if busy.
      }
      Spinner.show();

      //Submit geoprocessor task.
      geoprocessor.submitJob(Settings.exportMapGPServer_URL, params).then(function (jobInfo1) {
         Util.logMsg("Submitting geoprocessing task - Params:", params);

         //Wait for job to complete.
         jobInfo1.waitForJobCompletion({ interval: 3000 }).then(function (jobInfo2) {
            Util.logMsg("Geoprocessing task completed - JobInfo:", jobInfo2);
            jobComplete(jobInfo2);

         }, function (error: Error) {
            Spinner.hide();
            Util.errMsg("Geoprocessing task failed (waitForJobCompletion)", error);
         });

      }, function (error: Error) {
         Spinner.hide();
         Util.errMsg("Geoprocessing task failed (submitJob)", error);
      });

      //Job is complete.
      function jobComplete(jobInfo: JobInfo) {

         //Get the path to the exported file.
         jobInfo.fetchResultData("OutZipPath").then(function (result: __esri.ParameterValue) {
            Spinner.hide();

            const zipPath = result.value as string;
            Util.logMsg("Get results completed - Result: " + zipPath);

            if (zipPath.StartsWith("Error")) {
               Util.errMsg(zipPath);
               return;
            }

            if (zipPath.length < 20) {
               Util.errMsg("Zip file could not be created");
               return;
            }

            const url: string = Settings.webServerURL + "/" + zipPath.replace(/\\/g, "/");
            Util.logMsg("URL: " + url);

            //const sa: string[] = url.split("/");
            //const fileName: string = sa[sa.length - 1];
            //Util.downloadZip(fileName, url);
            window.location.href = url;

         }, function (error: Error) {
            Spinner.hide();
            Util.errMsg("Geoprocessing task failed (getResultData)", error);
         });
      }
   } //end downloadFile.

   //Styles.
   const styles: Default.iStyles = {
      toolStyle: { width: Settings.leftPanelWidth, maxWidth: Settings.leftPanelWidth, paddingBottom: 2 },
   };

   return (
      <Paper style={styles.toolStyle}>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div className="app-flex-row-around" style={{ marginTop: 5 }}>

            <TextField select style={{ marginTop: 7 }}
               label="Export Format"
               title="Export Format"
               inputProps={{ id: "export-format" }}
               InputLabelProps={{ htmlFor: "export-format" }}
               value={exportFormatValue}
               onChange={formatChanged}
            >
               {Util_MUI.buildSelectMenuItems(exportFormatLabels, exportFormatValues)}
            </TextField>

            <Button variant="outlined" color="info" onClick={downloadFile} title="Export map" aria-label="Export map">
               Export
            </Button>
         </div>

         <Typography variant="body2" style={{ margin: 10, marginTop: 20 }}>
            <b><i>Tip:</i></b>&nbsp;&nbsp;Exported data will be clipped to the current map extents.
         </Typography>

      </Paper>
   ); //end return.

} //end function.
